'use client';
import React, { useState } from 'react';
import { Shield, AlertTriangle, DollarSign, CheckCircle, XCircle, Clock, ChevronDown, FileText, Eye, Info, Building2, TrendingUp, Lock, BarChart3, Send, ThumbsUp, ThumbsDown, RefreshCw, ArrowRight, Zap, Activity, X, AlertCircle } from 'lucide-react';

import SectionHeader from '@/components/ui/SectionHeader';


// ─── Types ────────────────────────────────────────────────────────────────────
type ReviewStage = 'compliance' | 'risk' | 'finance';
type StageStatus = 'pending' | 'in-progress' | 'approved' | 'rejected' | 'conditional';
type DocStatus = 'verified' | 'pending' | 'flagged' | 'missing';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface ReviewDecision {
  stage: ReviewStage;
  status: StageStatus;
  reviewer: string;
  role: string;
  date: string | null;
  rationale: string;
  conditions: string[];
}

interface CampaignDoc {
  id: string;
  name: string;
  type: string;
  size: string;
  uploaded: string;
  status: DocStatus;
  notes?: string;
}

interface RiskFactor {
  id: string;
  label: string;
  severity: RiskLevel;
  detail: string;
}

interface FinanceItem {
  label: string;
  value: string;
  flagged?: boolean;
}

interface Campaign {
  id: string;
  name: string;
  issuer: string;
  sector: string;
  jurisdiction: string;
  offeringType: string;
  target: number;
  minInvestment: number;
  maxInvestment: number;
  created: string;
  submittedForReview: string;
  description: string;
  useOfFunds: string;
  documents: CampaignDoc[];
  riskFactors: RiskFactor[];
  financeItems: FinanceItem[];
  decisions: ReviewDecision[];
}

interface AuditEvent {
  ts: string;
  actor: string;
  role: string;
  action: string;
  detail: string;
  stage: ReviewStage | 'system';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const CAMPAIGNS: Campaign[] = [
  {
    id: 'CAMP-2026-004',
    name: 'Harborview Private Credit Fund',
    issuer: 'Harborview Capital DIFC',
    sector: 'Private Credit',
    jurisdiction: 'UAE (DIFC)',
    offeringType: 'Debt — Senior Secured',
    target: 40000000,
    minInvestment: 100000,
    maxInvestment: 5000000,
    created: '2026-06-20',
    submittedForReview: '2026-08-14',
    description: 'Senior secured private credit fund targeting SME lending across GCC. 5-year term, target 12% net IRR.',
    useOfFunds: '90% loan origination · 7% reserves · 3% fund expenses',
    documents: [
      { id: 'doc-301', name: 'Offering Circular v2.1', type: 'Disclosure', size: '6.4 MB', uploaded: '2026-07-01', status: 'verified' },
      { id: 'doc-302', name: 'Credit Policy & Underwriting Standards', type: 'Compliance', size: '3.2 MB', uploaded: '2026-07-05', status: 'verified' },
      { id: 'doc-303', name: 'Audited Financials FY2025', type: 'Financial', size: '4.8 MB', uploaded: '2026-07-08', status: 'verified' },
      { id: 'doc-304', name: 'Legal Opinion — DIFC Offering', type: 'Legal', size: '2.1 MB', uploaded: '2026-07-10', status: 'flagged', notes: 'Counsel qualification requires verification' },
      { id: 'doc-305', name: 'Subscription Agreement Template', type: 'Agreement', size: '1.0 MB', uploaded: '2026-07-12', status: 'verified' },
      { id: 'doc-306', name: 'AML/KYC Policy', type: 'Compliance', size: '0.9 MB', uploaded: '2026-07-15', status: 'pending' },
    ],
    riskFactors: [
      { id: 'rf-1', label: 'SME Credit Concentration', severity: 'high', detail: 'Top 10 borrowers represent 68% of projected loan book. Exceeds 50% concentration threshold.' },
      { id: 'rf-2', label: 'Geographic Exposure — GCC Only', severity: 'medium', detail: 'Single-region exposure with no diversification outside GCC. Macro risk elevated.' },
      { id: 'rf-3', label: 'Liquidity Risk — 5-Year Lock-up', severity: 'medium', detail: 'No secondary market mechanism. Investors locked for full 5-year term.' },
      { id: 'rf-4', label: 'Interest Rate Sensitivity', severity: 'low', detail: 'Floating rate loans partially hedge rate risk. Net exposure manageable.' },
      { id: 'rf-5', label: 'Regulatory Risk — DIFC Licensing', severity: 'low', detail: 'DIFC DFSA Category 3C license in good standing. No outstanding regulatory actions.' },
    ],
    financeItems: [
      { label: 'Target Raise', value: 'USD 40,000,000' },
      { label: 'Platform Fee', value: '2.0% of raise = USD 800,000' },
      { label: 'Management Fee', value: '1.5% p.a. on AUM' },
      { label: 'Performance Fee', value: '20% above 8% hurdle' },
      { label: 'Escrow Bank', value: 'Emirates NBD — DIFC Branch' },
      { label: 'Escrow Release Trigger', value: 'First close at 50% (USD 20M)', flagged: false },
      { label: 'Minimum Close Threshold', value: 'USD 15,000,000', flagged: true },
      { label: 'Subscription Period', value: '90 days from launch' },
      { label: 'Settlement Currency', value: 'USD' },
      { label: 'Waterfall Structure', value: 'Return of capital → 8% pref → 80/20 split' },
    ],
    decisions: [
      {
        stage: 'compliance',
        status: 'in-progress',
        reviewer: 'Fatima Al-Rashidi',
        role: 'Chief Compliance Officer',
        date: null,
        rationale: '',
        conditions: [],
      },
      {
        stage: 'risk',
        status: 'pending',
        reviewer: 'James Okonkwo',
        role: 'Chief Risk Officer',
        date: null,
        rationale: '',
        conditions: [],
      },
      {
        stage: 'finance',
        status: 'pending',
        reviewer: 'Priya Nair',
        role: 'Chief Financial Officer',
        date: null,
        rationale: '',
        conditions: [],
      },
    ],
  },
  {
    id: 'CAMP-2026-002',
    name: 'Solaris Renewable Energy Fund III',
    issuer: 'Solaris Asset Management DIFC',
    sector: 'Renewable Energy',
    jurisdiction: 'UAE (ADGM)',
    offeringType: 'Fund Units — Reg S',
    target: 25000000,
    minInvestment: 50000,
    maxInvestment: 2000000,
    created: '2026-07-15',
    submittedForReview: '2026-08-01',
    description: 'Closed-end fund targeting utility-scale solar projects across MENA. 7-year term with quarterly distributions.',
    useOfFunds: '80% project equity · 12% working capital · 8% fund expenses',
    documents: [
      { id: 'doc-101', name: 'Fund Prospectus v1.4', type: 'Disclosure', size: '5.1 MB', uploaded: '2026-07-20', status: 'verified' },
      { id: 'doc-102', name: 'Risk Disclosure Statement', type: 'Disclosure', size: '1.2 MB', uploaded: '2026-07-20', status: 'verified' },
      { id: 'doc-103', name: 'Audited Financials FY2025', type: 'Financial', size: '4.0 MB', uploaded: '2026-07-22', status: 'verified' },
      { id: 'doc-104', name: 'Environmental Impact Assessment', type: 'Due Diligence', size: '7.3 MB', uploaded: '2026-07-25', status: 'verified' },
      { id: 'doc-105', name: 'Subscription Agreement', type: 'Agreement', size: '1.1 MB', uploaded: '2026-07-28', status: 'verified' },
      { id: 'doc-106', name: 'AML/KYC Policy Document', type: 'Compliance', size: '0.8 MB', uploaded: '2026-07-28', status: 'pending' },
    ],
    riskFactors: [
      { id: 'rf-1', label: 'MENA Geographic Concentration', severity: 'high', detail: 'All projects located in MENA. Geopolitical and regulatory risk concentrated.' },
      { id: 'rf-2', label: 'Construction Risk', severity: 'medium', detail: 'Two projects in early construction phase. Completion risk partially mitigated by EPC contracts.' },
      { id: 'rf-3', label: 'Currency Risk', severity: 'low', detail: 'USD-denominated fund with local currency revenue. Hedging strategy in place.' },
    ],
    financeItems: [
      { label: 'Target Raise', value: 'USD 25,000,000' },
      { label: 'Platform Fee', value: '1.75% of raise = USD 437,500' },
      { label: 'Management Fee', value: '2.0% p.a. on committed capital' },
      { label: 'Performance Fee', value: '20% above 10% hurdle' },
      { label: 'Escrow Bank', value: 'Abu Dhabi Commercial Bank — ADGM' },
      { label: 'Escrow Release Trigger', value: 'First close at 40% (USD 10M)' },
      { label: 'Minimum Close Threshold', value: 'USD 8,000,000' },
      { label: 'Subscription Period', value: '120 days from launch' },
      { label: 'Settlement Currency', value: 'USD' },
      { label: 'Waterfall Structure', value: 'Return of capital → 10% pref → 80/20 split' },
    ],
    decisions: [
      {
        stage: 'compliance',
        status: 'approved',
        reviewer: 'Fatima Al-Rashidi',
        role: 'Chief Compliance Officer',
        date: '2026-08-05',
        rationale: 'Prospectus reviewed. Minor amendment required on risk factor disclosures — resubmitted and cleared. All regulatory requirements met for ADGM offering.',
        conditions: ['Risk factor disclosures updated per FSRA guidance'],
      },
      {
        stage: 'risk',
        status: 'in-progress',
        reviewer: 'James Okonkwo',
        role: 'Chief Risk Officer',
        date: null,
        rationale: '',
        conditions: [],
      },
      {
        stage: 'finance',
        status: 'pending',
        reviewer: 'Priya Nair',
        role: 'Chief Financial Officer',
        date: null,
        rationale: '',
        conditions: [],
      },
    ],
  },
];

const INITIAL_AUDIT: AuditEvent[] = [
  { ts: '2026-08-14 08:30', actor: 'System', role: 'Automated', action: 'Review Initiated', detail: 'Campaign CAMP-2026-004 submitted for multi-stage review. Assigned to Compliance queue.', stage: 'system' },
  { ts: '2026-08-14 09:15', actor: 'Fatima Al-Rashidi', role: 'CCO', action: 'Compliance Review Started', detail: 'Opened campaign review. Began document verification.', stage: 'compliance' },
  { ts: '2026-08-14 11:02', actor: 'Fatima Al-Rashidi', role: 'CCO', action: 'Document Flagged', detail: 'Legal Opinion doc-304 flagged — counsel qualification requires verification.', stage: 'compliance' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : `$${(n / 1_000).toFixed(0)}K`;

const stageConfig: Record<ReviewStage, { label: string; icon: React.ElementType; color: string; bg: string; border: string }> = {
  compliance: { label: 'Compliance Review', icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  risk: { label: 'Risk Review', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  finance: { label: 'Finance Review', icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
};

const statusConfig: Record<StageStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  pending: { label: 'Pending', color: 'text-foreground-muted', bg: 'bg-surface-3', icon: Clock },
  'in-progress': { label: 'In Progress', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: RefreshCw },
  approved: { label: 'Approved', color: 'text-success', bg: 'bg-success/10', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-critical', bg: 'bg-critical/10', icon: XCircle },
  conditional: { label: 'Conditional', color: 'text-warning', bg: 'bg-warning/10', icon: AlertCircle },
};

const riskColors: Record<RiskLevel, string> = {
  low: 'text-success bg-success/10 border-success/30',
  medium: 'text-warning bg-warning/10 border-warning/30',
  high: 'text-critical bg-critical/10 border-critical/30',
  critical: 'text-critical bg-critical/20 border-critical/50',
};

const docStatusConfig: Record<DocStatus, { label: string; color: string }> = {
  verified: { label: 'Verified', color: 'text-success' },
  pending: { label: 'Pending', color: 'text-warning' },
  flagged: { label: 'Flagged', color: 'text-critical' },
  missing: { label: 'Missing', color: 'text-critical' },
};

// ─── Stage Gate Component ─────────────────────────────────────────────────────
interface StageGateProps {
  stage: ReviewStage;
  decision: ReviewDecision;
  isActive: boolean;
  isLocked: boolean;
  onDecision: (stage: ReviewStage, status: StageStatus, rationale: string, conditions: string[]) => void;
}

function StageGate({ stage, decision, isActive, isLocked, onDecision }: StageGateProps) {
  const [expanded, setExpanded] = useState(isActive);
  const [localRationale, setLocalRationale] = useState(decision.rationale);
  const [localConditions, setLocalConditions] = useState<string[]>(decision.conditions);
  const [newCondition, setNewCondition] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cfg = stageConfig[stage];
  const statusCfg = statusConfig[decision.status];
  const StageIcon = cfg.icon;
  const StatusIcon = statusCfg.icon;

  const handleSubmit = (status: StageStatus) => {
    if (!localRationale.trim()) return;
    setSubmitting(true);
    setTimeout(() => {
      onDecision(stage, status, localRationale, localConditions);
      setSubmitting(false);
    }, 600);
  };

  const addCondition = () => {
    if (newCondition.trim()) {
      setLocalConditions(prev => [...prev, newCondition.trim()]);
      setNewCondition('');
    }
  };

  const removeCondition = (idx: number) => {
    setLocalConditions(prev => prev.filter((_, i) => i !== idx));
  };

  const isDecided = decision.status === 'approved' || decision.status === 'rejected' || decision.status === 'conditional';

  return (
    <div className={`rounded-xl border transition-all duration-200 ${
      isActive ? `${cfg.border} ${cfg.bg}` : isDecided ? 'border-border bg-surface-2' : 'border-border/50 bg-surface-2/50'
    } ${isLocked ? 'opacity-50' : ''}`}>
      {/* Gate Header */}
      <button
        className="w-full flex items-center gap-4 p-5 text-left"
        onClick={() => !isLocked && setExpanded(e => !e)}
        disabled={isLocked}
      >
        {/* Stage icon */}
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg} border ${cfg.border}`}>
          <StageIcon size={18} className={cfg.color} />
        </div>

        {/* Stage info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground text-sm">{cfg.label}</span>
            {isLocked && (
              <span className="flex items-center gap-1 text-xs text-foreground-muted">
                <Lock size={11} /> Locked — prior stage pending
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-xs text-foreground-muted">{decision.reviewer} · {decision.role}</span>
            {decision.date && <span className="text-xs text-foreground-muted">{decision.date}</span>}
          </div>
        </div>

        {/* Status badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color} border ${
          decision.status === 'approved' ? 'border-success/30' :
          decision.status === 'rejected' ? 'border-critical/30' :
          decision.status === 'conditional' ? 'border-warning/30' :
          decision.status === 'in-progress'? 'border-blue-500/30' : 'border-border'
        }`}>
          <StatusIcon size={12} />
          {statusCfg.label}
        </div>

        {!isLocked && (
          <ChevronDown size={16} className={`text-foreground-muted transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Gate Body */}
      {expanded && !isLocked && (
        <div className="px-5 pb-5 border-t border-border/50 pt-4 space-y-4">
          {/* Decided state — show summary */}
          {isDecided ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-1">Decision Rationale</p>
                <p className="text-sm text-foreground bg-surface-3 rounded-lg p-3">{decision.rationale}</p>
              </div>
              {decision.conditions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-foreground-muted uppercase tracking-wider mb-2">Conditions Attached</p>
                  <div className="space-y-1.5">
                    {decision.conditions.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-warning bg-warning/5 border border-warning/20 rounded-lg px-3 py-2">
                        <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                        {c}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Active decision form */
            <div className="space-y-4">
              {/* Rationale */}
              <div>
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wider block mb-1.5">
                  Decision Rationale <span className="text-critical">*</span>
                </label>
                <textarea
                  value={localRationale}
                  onChange={e => setLocalRationale(e.target.value)}
                  placeholder={`Provide your ${cfg.label.toLowerCase()} rationale…`}
                  rows={3}
                  className="w-full bg-surface-3 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-foreground-muted resize-none focus:outline-none focus:border-blue-500/50 transition-colors"
                />
              </div>

              {/* Conditions */}
              <div>
                <label className="text-xs font-medium text-foreground-muted uppercase tracking-wider block mb-1.5">
                  Conditions (optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCondition}
                    onChange={e => setNewCondition(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCondition()}
                    placeholder="Add a condition or requirement…"
                    className="flex-1 bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-blue-500/50 transition-colors"
                  />
                  <button
                    onClick={addCondition}
                    className="px-3 py-2 bg-surface-3 border border-border rounded-lg text-sm text-foreground-muted hover:text-foreground hover:border-border-strong transition-colors"
                  >
                    Add
                  </button>
                </div>
                {localConditions.length > 0 && (
                  <div className="space-y-1.5">
                    {localConditions.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-warning bg-warning/5 border border-warning/20 rounded-lg px-3 py-2">
                        <AlertCircle size={13} className="flex-shrink-0" />
                        <span className="flex-1">{c}</span>
                        <button onClick={() => removeCondition(i)} className="text-foreground-muted hover:text-critical transition-colors">
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Decision buttons */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => handleSubmit('approved')}
                  disabled={!localRationale.trim() || submitting}
                  className="flex items-center gap-2 px-4 py-2.5 bg-success/10 border border-success/30 text-success rounded-lg text-sm font-medium hover:bg-success/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ThumbsUp size={14} />
                  Approve
                </button>
                <button
                  onClick={() => handleSubmit('conditional')}
                  disabled={!localRationale.trim() || localConditions.length === 0 || submitting}
                  className="flex items-center gap-2 px-4 py-2.5 bg-warning/10 border border-warning/30 text-warning rounded-lg text-sm font-medium hover:bg-warning/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={localConditions.length === 0 ? 'Add at least one condition' : ''}
                >
                  <AlertCircle size={14} />
                  Approve with Conditions
                </button>
                <button
                  onClick={() => handleSubmit('rejected')}
                  disabled={!localRationale.trim() || submitting}
                  className="flex items-center gap-2 px-4 py-2.5 bg-critical/10 border border-critical/30 text-critical rounded-lg text-sm font-medium hover:bg-critical/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ThumbsDown size={14} />
                  Reject
                </button>
                {submitting && <RefreshCw size={14} className="text-foreground-muted animate-spin" />}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CampaignReviewWorkflow() {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>(CAMPAIGNS[0].id);
  const [campaigns, setCampaigns] = useState<Campaign[]>(CAMPAIGNS);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'risk' | 'finance' | 'gates'>('gates');
  const [auditLog, setAuditLog] = useState<AuditEvent[]>(INITIAL_AUDIT);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);

  const campaign = campaigns.find(c => c.id === selectedCampaignId)!;

  const getStageOrder = (): ReviewStage[] => ['compliance', 'risk', 'finance'];

  const isStageActive = (stage: ReviewStage): boolean => {
    const order = getStageOrder();
    const idx = order.indexOf(stage);
    if (idx === 0) return true;
    const prev = order[idx - 1];
    const prevDecision = campaign.decisions.find(d => d.stage === prev);
    return prevDecision?.status === 'approved' || prevDecision?.status === 'conditional';
  };

  const isStageInProgress = (stage: ReviewStage): boolean => {
    const d = campaign.decisions.find(d => d.stage === stage);
    return d?.status === 'in-progress';
  };

  const overallStatus = (): { label: string; color: string } => {
    const decisions = campaign.decisions;
    if (decisions.every(d => d.status === 'approved' || d.status === 'conditional')) {
      return { label: 'All Stages Approved', color: 'text-success' };
    }
    if (decisions.some(d => d.status === 'rejected')) {
      return { label: 'Review Rejected', color: 'text-critical' };
    }
    if (decisions.some(d => d.status === 'in-progress')) {
      return { label: 'Review In Progress', color: 'text-blue-400' };
    }
    return { label: 'Awaiting Review', color: 'text-foreground-muted' };
  };

  const handleDecision = (stage: ReviewStage, status: StageStatus, rationale: string, conditions: string[]) => {
    const now = '2026-08-20';
    const stageLabels: Record<ReviewStage, string> = {
      compliance: 'Compliance',
      risk: 'Risk',
      finance: 'Finance',
    };
    const reviewerMap: Record<ReviewStage, string> = {
      compliance: 'Fatima Al-Rashidi',
      risk: 'James Okonkwo',
      finance: 'Priya Nair',
    };
    const roleMap: Record<ReviewStage, string> = {
      compliance: 'CCO',
      risk: 'CRO',
      finance: 'CFO',
    };

    setCampaigns(prev =>
      prev.map(c => {
        if (c.id !== selectedCampaignId) return c;
        const updatedDecisions = c.decisions.map(d => {
          if (d.stage !== stage) return d;
          return { ...d, status, rationale, conditions, date: now };
        });
        // Auto-set next stage to in-progress
        const order = getStageOrder();
        const idx = order.indexOf(stage);
        const nextStage = order[idx + 1];
        const finalDecisions = updatedDecisions.map(d => {
          if (nextStage && d.stage === nextStage && (status === 'approved' || status === 'conditional')) {
            return { ...d, status: 'in-progress' as StageStatus };
          }
          return d;
        });
        return { ...c, decisions: finalDecisions };
      })
    );

    const actionLabel = status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Conditionally Approved';
    setAuditLog(prev => [
      ...prev,
      {
        ts: `2026-08-20 ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
        actor: reviewerMap[stage],
        role: roleMap[stage],
        action: `${stageLabels[stage]} Review ${actionLabel}`,
        detail: rationale,
        stage,
      },
    ]);
  };

  const completedStages = campaign.decisions.filter(d => d.status === 'approved' || d.status === 'conditional' || d.status === 'rejected').length;
  const approvedStages = campaign.decisions.filter(d => d.status === 'approved' || d.status === 'conditional').length;
  const docVerified = campaign.documents.filter(d => d.status === 'verified').length;
  const highRiskFactors = campaign.riskFactors.filter(r => r.severity === 'high' || r.severity === 'critical').length;

  const tabs = [
    { id: 'gates', label: 'Review Gates', icon: Shield },
    { id: 'overview', label: 'Campaign', icon: Building2 },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'risk', label: 'Risk Factors', icon: AlertTriangle },
    { id: 'finance', label: 'Finance', icon: DollarSign },
  ] as const;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Page Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
        <SectionHeader
          title="Campaign Review Workflow"
          subtitle="Multi-stage approval gates — Compliance · Risk · Finance"
          icon={<Shield size={20} className="text-blue-400" />}
        />
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: Campaign List */}
        <div className="w-72 flex-shrink-0 border-r border-border flex flex-col bg-surface-2">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">Campaigns Pending Review</p>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {campaigns.map(c => {
              const approved = c.decisions.filter(d => d.status === 'approved' || d.status === 'conditional').length;
              const rejected = c.decisions.some(d => d.status === 'rejected');
              const inProgress = c.decisions.some(d => d.status === 'in-progress');
              const isSelected = c.id === selectedCampaignId;

              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedCampaignId(c.id)}
                  className={`w-full text-left px-4 py-3 transition-colors border-l-2 ${
                    isSelected
                      ? 'bg-surface-3 border-l-blue-500' :'border-l-transparent hover:bg-surface-3/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground leading-tight line-clamp-2">{c.name}</span>
                    {rejected ? (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-critical mt-1.5" />
                    ) : approved === 3 ? (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-success mt-1.5" />
                    ) : inProgress ? (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-400 mt-1.5 animate-pulse" />
                    ) : (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-foreground-muted/30 mt-1.5" />
                    )}
                  </div>
                  <p className="text-xs text-foreground-muted truncate">{c.issuer}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex gap-1">
                      {getStageOrder().map(stage => {
                        const d = c.decisions.find(dec => dec.stage === stage);
                        const s = d?.status ?? 'pending';
                        return (
                          <div
                            key={stage}
                            className={`w-5 h-1.5 rounded-full ${
                              s === 'approved' || s === 'conditional' ? 'bg-success' :
                              s === 'rejected' ? 'bg-critical' :
                              s === 'in-progress'? 'bg-blue-400' : 'bg-surface-3'
                            }`}
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs text-foreground-muted">{approved}/3 approved</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Campaign Header */}
          <div className="px-6 py-4 border-b border-border bg-surface-2 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-foreground-muted">{campaign.id}</span>
                  <span className="text-foreground-muted">·</span>
                  <span className={`text-xs font-medium ${overallStatus().color}`}>{overallStatus().label}</span>
                </div>
                <h2 className="text-lg font-bold text-foreground">{campaign.name}</h2>
                <p className="text-sm text-foreground-muted mt-0.5">{campaign.issuer} · {campaign.sector} · {campaign.jurisdiction}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-foreground-muted">Submitted {campaign.submittedForReview}</span>
              </div>
            </div>

            {/* KPI Strip */}
            <div className="grid grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Target Raise', value: fmt(campaign.target), icon: TrendingUp, color: 'text-foreground' },
                { label: 'Stages Complete', value: `${completedStages}/3`, icon: CheckCircle, color: completedStages === 3 ? 'text-success' : 'text-foreground' },
                { label: 'Docs Verified', value: `${docVerified}/${campaign.documents.length}`, icon: FileText, color: docVerified === campaign.documents.length ? 'text-success' : 'text-warning' },
                { label: 'High Risk Factors', value: String(highRiskFactors), icon: AlertTriangle, color: highRiskFactors > 0 ? 'text-critical' : 'text-success' },
              ].map(kpi => (
                <div key={kpi.label} className="bg-surface-3 rounded-lg px-3 py-2.5 flex items-center gap-2.5">
                  <kpi.icon size={15} className={kpi.color} />
                  <div>
                    <p className={`text-sm font-semibold ${kpi.color}`}>{kpi.value}</p>
                    <p className="text-xs text-foreground-muted">{kpi.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border bg-surface-2 flex-shrink-0 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400' :'border-transparent text-foreground-muted hover:text-foreground'
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex gap-6 p-6 min-h-full">
              {/* Main panel */}
              <div className="flex-1 min-w-0 space-y-4">
                {/* ── Review Gates Tab ── */}
                {activeTab === 'gates' && (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Info size={14} className="text-foreground-muted" />
                      <p className="text-xs text-foreground-muted">Each stage must be approved before the next gate unlocks. Rejected campaigns are returned to the issuer.</p>
                    </div>
                    {/* Pipeline visual */}
                    <div className="flex items-center gap-2 mb-4">
                      {getStageOrder().map((stage, idx) => {
                        const d = campaign.decisions.find(dec => dec.stage === stage)!;
                        const cfg = stageConfig[stage];
                        const StageIcon = cfg.icon;
                        const isLast = idx === 2;
                        return (
                          <React.Fragment key={stage}>
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
                              d.status === 'approved' || d.status === 'conditional' ? 'bg-success/10 border-success/30 text-success' :
                              d.status === 'rejected' ? 'bg-critical/10 border-critical/30 text-critical' :
                              d.status === 'in-progress' ? `${cfg.bg} ${cfg.border} ${cfg.color}` :
                              'bg-surface-3 border-border text-foreground-muted'
                            }`}>
                              <StageIcon size={13} />
                              {cfg.label}
                            </div>
                            {!isLast && (
                              <ArrowRight size={14} className={`flex-shrink-0 ${
                                d.status === 'approved' || d.status === 'conditional' ? 'text-success' : 'text-foreground-muted/30'
                              }`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                      <ArrowRight size={14} className={`flex-shrink-0 ${
                        campaign.decisions.every(d => d.status === 'approved' || d.status === 'conditional') ? 'text-success' : 'text-foreground-muted/30'
                      }`} />
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
                        campaign.decisions.every(d => d.status === 'approved' || d.status === 'conditional')
                          ? 'bg-success/10 border-success/30 text-success' :'bg-surface-3 border-border text-foreground-muted'
                      }`}>
                        <Zap size={13} />
                        Published
                      </div>
                    </div>

                    {/* Stage gates */}
                    <div className="space-y-3">
                      {getStageOrder().map(stage => {
                        const d = campaign.decisions.find(dec => dec.stage === stage)!;
                        const active = isStageActive(stage);
                        return (
                          <StageGate
                            key={stage}
                            stage={stage}
                            decision={d}
                            isActive={active && (d.status === 'in-progress' || d.status === 'pending')}
                            isLocked={!active}
                            onDecision={handleDecision}
                          />
                        );
                      })}
                    </div>

                    {/* Publish gate */}
                    {campaign.decisions.every(d => d.status === 'approved' || d.status === 'conditional') && (
                      <div className="rounded-xl border border-success/30 bg-success/5 p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-success/10 border border-success/30 flex items-center justify-center">
                            <Zap size={18} className="text-success" />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">All Review Gates Cleared</p>
                            <p className="text-xs text-foreground-muted">Campaign is eligible for publication</p>
                          </div>
                        </div>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-success text-white rounded-lg text-sm font-semibold hover:bg-success/90 transition-colors">
                          <Send size={14} />
                          Publish Campaign
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* ── Campaign Overview Tab ── */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="bg-surface-2 rounded-xl border border-border p-5">
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Campaign Details</p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {[
                          { label: 'Offering Type', value: campaign.offeringType },
                          { label: 'Jurisdiction', value: campaign.jurisdiction },
                          { label: 'Target Raise', value: fmt(campaign.target) },
                          { label: 'Min Investment', value: fmt(campaign.minInvestment) },
                          { label: 'Max Investment', value: fmt(campaign.maxInvestment) },
                          { label: 'Sector', value: campaign.sector },
                          { label: 'Created', value: campaign.created },
                          { label: 'Submitted for Review', value: campaign.submittedForReview },
                        ].map(item => (
                          <div key={item.label}>
                            <p className="text-xs text-foreground-muted">{item.label}</p>
                            <p className="text-sm font-medium text-foreground mt-0.5">{item.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-surface-2 rounded-xl border border-border p-5">
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-2">Description</p>
                      <p className="text-sm text-foreground leading-relaxed">{campaign.description}</p>
                    </div>
                    <div className="bg-surface-2 rounded-xl border border-border p-5">
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-2">Use of Funds</p>
                      <p className="text-sm text-foreground">{campaign.useOfFunds}</p>
                    </div>
                  </div>
                )}

                {/* ── Documents Tab ── */}
                {activeTab === 'documents' && (
                  <div className="space-y-2">
                    {campaign.documents.map(doc => {
                      const statusCfg = docStatusConfig[doc.status];
                      const isExpanded = expandedDoc === doc.id;
                      return (
                        <div key={doc.id} className={`rounded-xl border transition-all ${
                          doc.status === 'flagged' ? 'border-critical/30 bg-critical/5' :
                          doc.status === 'pending'? 'border-warning/30 bg-warning/5' : 'border-border bg-surface-2'
                        }`}>
                          <button
                            className="w-full flex items-center gap-3 px-4 py-3 text-left"
                            onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                          >
                            <FileText size={15} className="text-foreground-muted flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
                              <p className="text-xs text-foreground-muted">{doc.type} · {doc.size} · {doc.uploaded}</p>
                            </div>
                            <span className={`text-xs font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                            <ChevronDown size={14} className={`text-foreground-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-3 border-t border-border/50 pt-3 flex items-center justify-between gap-3">
                              {doc.notes ? (
                                <p className="text-xs text-critical flex items-start gap-1.5">
                                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                                  {doc.notes}
                                </p>
                              ) : (
                                <p className="text-xs text-foreground-muted">No reviewer notes.</p>
                              )}
                              <button className="flex items-center gap-1.5 text-xs text-foreground-muted hover:text-foreground transition-colors flex-shrink-0">
                                <Eye size={13} /> View
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Risk Factors Tab ── */}
                {activeTab === 'risk' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 bg-surface-2 rounded-lg border border-border">
                      <BarChart3 size={14} className="text-foreground-muted" />
                      <span className="text-xs text-foreground-muted">
                        {campaign.riskFactors.filter(r => r.severity === 'high' || r.severity === 'critical').length} high/critical ·{' '}
                        {campaign.riskFactors.filter(r => r.severity === 'medium').length} medium ·{' '}
                        {campaign.riskFactors.filter(r => r.severity === 'low').length} low risk factors identified
                      </span>
                    </div>
                    {campaign.riskFactors.map(rf => (
                      <div key={rf.id} className={`rounded-xl border p-4 ${riskColors[rf.severity]}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <p className="text-sm font-semibold">{rf.label}</p>
                          <span className={`text-xs font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border ${riskColors[rf.severity]}`}>
                            {rf.severity}
                          </span>
                        </div>
                        <p className="text-xs opacity-80 leading-relaxed">{rf.detail}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Finance Tab ── */}
                {activeTab === 'finance' && (
                  <div className="bg-surface-2 rounded-xl border border-border overflow-hidden">
                    <div className="px-5 py-3 border-b border-border">
                      <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">Financial Structure & Escrow Terms</p>
                    </div>
                    <div className="divide-y divide-border">
                      {campaign.financeItems.map((item, i) => (
                        <div key={i} className={`flex items-center justify-between px-5 py-3 ${item.flagged ? 'bg-warning/5' : ''}`}>
                          <span className="text-sm text-foreground-muted">{item.label}</span>
                          <div className="flex items-center gap-2">
                            {item.flagged && <AlertCircle size={13} className="text-warning" />}
                            <span className={`text-sm font-medium ${item.flagged ? 'text-warning' : 'text-foreground'}`}>{item.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Audit Trail Sidebar */}
              <div className="w-72 flex-shrink-0">
                <div className="bg-surface-2 rounded-xl border border-border overflow-hidden sticky top-0">
                  <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Activity size={14} className="text-foreground-muted" />
                    <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider">Audit Trail</p>
                  </div>
                  <div className="divide-y divide-border max-h-[calc(100vh-380px)] overflow-y-auto">
                    {[...auditLog].reverse().map((event, i) => {
                      const stageCfg = event.stage !== 'system' ? stageConfig[event.stage as ReviewStage] : null;
                      return (
                        <div key={i} className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                              event.stage === 'compliance' ? 'bg-blue-400' :
                              event.stage === 'risk' ? 'bg-amber-400' :
                              event.stage === 'finance'? 'bg-emerald-400' : 'bg-foreground-muted/40'
                            }`} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-foreground">{event.action}</p>
                              <p className="text-xs text-foreground-muted mt-0.5 leading-relaxed line-clamp-2">{event.detail}</p>
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-xs text-foreground-muted/70">{event.actor}</span>
                                <span className="text-foreground-muted/40">·</span>
                                <span className="text-xs text-foreground-muted/70 font-mono">{event.ts.split(' ')[1]}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
